import {
  useState,
  ComponentProps,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { Text, Box } from '@chakra-ui/react';
import {
  AsyncSelect,
  chakraComponents,
  GroupBase,
  MultiValue,
  ActionMeta,
} from 'chakra-react-select';
import { SelectComponents } from 'react-select/dist/declarations/src/components';
import { useQueryClient } from 'react-query';
import { useAppContext } from 'src/contexts/AppContext';
import {
  CampaignMapping,
  CampaignMappingSource,
  fetchCampaignSources,
  useUpdateCampaignMappingSourceMutation,
} from 'src/api/campaignMappings';
import { groupOptionsWithSuggestions } from 'src/features/CampaignMapping/utils/campaingMapping';

export type Option = {
  label: string;
  value: string;
  source: string;
  table: string;
};
export type Group = GroupBase<Option>;

type CampaignMappingDropdown = {
  value?: Option[] | null;
  column: string;
  campaignRow: CampaignMapping;
  placeholder?: string;
  onClose: () => void;
  isOpen: boolean;
};

export const CampaignMappingDropdown: React.FC<CampaignMappingDropdown> = ({
  value,
  column,
  campaignRow,
  placeholder = 'Select',
  onClose,
  isOpen,
}) => {
  const [isFetchingOptions, setIsFetchingOptions] = useState(false);
  const [defaultGroups, setDefaultGroups] = useState<Group[] | false>(false);

  const abortRef = useRef<AbortController | null>(null);
  const ref = useRef(null);

  const queryClient = useQueryClient();
  const { currentCustomer } = useAppContext();
  const updateSourceMutation = useUpdateCampaignMappingSourceMutation();

  const loadOptions = useCallback(
    async (_inputValue: string): Promise<Group[]> => {
      const ac = new AbortController();
      abortRef.current = ac;

      // debounce for user input
      await new Promise((r) => setTimeout(r, 180));
      try {
        setIsFetchingOptions(true);
        const customer = currentCustomer?.customerName ?? 'missing_customer';
        const queryKey = ['useCampaignSourcesQuery', customer, column];
        const cachedSources =
          queryClient.getQueryData<CampaignMappingSource[]>(queryKey);
        const sources =
          cachedSources ??
          (await queryClient.fetchQuery(
            queryKey,
            () => fetchCampaignSources(customer, column, ac.signal),
            { retry: false },
          ));
        const options = (sources ?? []).map((source) => ({
          label: source.campaign_name,
          value: source.id ?? source.source_campaign_id ?? source.campaign_name,
          source: source.source,
          table: source.table_name ?? '',
        }));

        return groupOptionsWithSuggestions(options, campaignRow.campaign_name);
      } catch (e) {
        if ((e as { name: string }).name === 'AbortError') return [];
        // eslint-disable-next-line no-console
        console.error('campaign fetch failed', e);
        return [];
      } finally {
        setIsFetchingOptions(false);
      }
    },
    [
      currentCustomer?.customerName,
      column,
      queryClient,
      campaignRow.campaign_name,
    ],
  );

  const onMenuOpen = useCallback(async () => {
    if (defaultGroups !== false) return;
    const groups = await loadOptions('');
    if (Array.isArray(groups)) setDefaultGroups(groups);
  }, [defaultGroups, loadOptions]);

  useEffect(() => {
    if (!isOpen) return;
    onMenuOpen();
    // Auto-focus the select input so onBlur works when clicking outside
    setTimeout(() => {
      if (ref.current && isOpen) {
        (ref.current as HTMLInputElement).focus();
      }
    }, 500);
  }, [isOpen, onMenuOpen]);

  const components = {
    MultiValueContainer: (
      props: ComponentProps<typeof chakraComponents.MultiValueContainer>,
    ) => {
      const selectedValues = (props.selectProps.value as Option[]) || [];
      // Only show our custom text for the first item by comparing with the first selected value
      if (
        props.data &&
        selectedValues.length > 0 &&
        props.data.value === selectedValues[0].value
      ) {
        return (
          <Box display="inline-flex" alignItems="center">
            <Text color="text.muted" fontSize="sm">
              {selectedValues.length} selected
            </Text>
          </Box>
        );
      }

      // Hide all other multi-value containers
      return null;
    },
  };

  const handleCampaignChange = useCallback(
    (
      _newSelectedOptions: MultiValue<Option>,
      actionMeta: ActionMeta<Option>,
    ) => {
      const customer = currentCustomer?.customerName ?? 'missing_customer';
      const { action, option } = actionMeta;

      if (!option) return;

      if (action === 'select-option') {
        // Assign source to this campaign mapping
        updateSourceMutation.mutate({
          customer,
          campaignMappingId: campaignRow.campaign_id,
          sourceId: option.value,
        });
      } else if (action === 'deselect-option') {
        // Deselect source (remove from all mappings)
        updateSourceMutation.mutate({
          customer,
          campaignMappingId: null,
          sourceId: option.value,
        });
      }
    },
    [
      currentCustomer?.customerName,
      campaignRow.campaign_id,
      updateSourceMutation,
    ],
  );

  return (
    <AsyncSelect<Option, true, Group>
      cacheOptions
      defaultOptions={defaultGroups}
      loadOptions={loadOptions}
      isSearchable
      ref={ref}
      isLoading={isFetchingOptions || updateSourceMutation.isLoading}
      isDisabled={isFetchingOptions || updateSourceMutation.isLoading}
      closeMenuOnSelect={false}
      blurInputOnSelect={false}
      hideSelectedOptions={false}
      value={value ?? null}
      onChange={handleCampaignChange}
      onMenuOpen={onMenuOpen}
      onMenuClose={onClose}
      onBlur={onClose}
      menuIsOpen={isOpen}
      menuPortalTarget={document.body}
      isMulti
      components={components as Partial<SelectComponents<Option, true, Group>>}
      size="sm"
      selectedOptionStyle="check"
      chakraStyles={{
        menu: (provided) => ({
          ...provided,
          zIndex: 9999,
          width: 'max-content',
          minWidth: '200px',
          maxWidth: '500px',
        }),
        menuList: (provided) => ({
          ...provided,
          backgroundColor: 'surface.overlay',
          bg: 'surface.overlay',
        }),
        option: (provided, state) => ({
          ...provided,
          backgroundColor: state.isFocused
            ? 'surface.hover'
            : 'surface.overlay',
          bg: state.isFocused ? 'surface.hover' : 'surface.overlay',
        }),
        valueContainer: (provided) => ({
          ...provided,
          paddingEnd: '0px',
        }),
        control: (provided, state) => {
          const currentValue = state.selectProps.value as Option[] | null;
          const hasSelection =
            Array.isArray(currentValue) && currentValue.length > 0;
          return {
            ...provided,
            border: hasSelection ? '1px solid' : provided.border,
            borderColor: hasSelection ? 'icon.green' : 'border.box',
            _hover: {
              borderColor: hasSelection ? 'icon.green' : 'border.box',
            },
          };
        },
      }}
      isClearable={false}
      placeholder={placeholder}
      loadingMessage={() => 'Loading…'}
      noOptionsMessage={() => 'No results'}
    />
  );
};
